import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';

import {
  AppRoutes,
  EMAIL_VALIDATION_REGEX,
  LOGIN_INPUT_CLASSES,
  LOGIN_SUBMIT_BUTTON_CLASSES,
} from '@/constants';
import { useLoginWithCredentials } from '@/hooks';
import { ILoginWithCredentialsPayload } from '@/interfaces';
import { navigate } from '@/providers';
import { getApiErrorMessage } from '@/utils';

import { Loading02Icon } from '@/assets/icons';
import { AuthGlassContainer, Button, Input } from '@/components/common';

import { LOGIN_FORM_FIELDS, LOGIN_SCHEMA } from '@/features/auth/constants';

interface ICredentials {
  onCredentialsSuccess: (email: string) => void;
}

export const Credentials = ({ onCredentialsSuccess }: ICredentials) => {
  const mutation = useLoginWithCredentials();

  const {
    register,
    handleSubmit,
    setError,
    watch,
    getFieldState,
    formState: { errors },
  } = useForm<ILoginWithCredentialsPayload>({
    resolver: yupResolver(LOGIN_SCHEMA),
    mode: 'onTouched',
  });

  const emailState = getFieldState(LOGIN_FORM_FIELDS.username);
  const emailValue = watch(LOGIN_FORM_FIELDS.username) ?? '';
  const isValidEmailFormat = EMAIL_VALIDATION_REGEX.test(emailValue);
  const showEmailSuccessIcon = emailState.isTouched && !emailState.error && isValidEmailFormat;

  const onSubmit = async (data: ILoginWithCredentialsPayload) => {
    await mutation.mutateAsync(data, {
      onSuccess: () => {
        onCredentialsSuccess(data.username);
      },
      onError: (error) => {
        const apiMessage = getApiErrorMessage(error);
        if (apiMessage) {
          setError(LOGIN_FORM_FIELDS.username, { type: 'manual', message: ' ' });
          setError(LOGIN_FORM_FIELDS.password, { type: 'manual', message: apiMessage });
        }
      },
    });
  };

  return (
    <AuthGlassContainer className="p-8 flex flex-col gap-8 w-[400px]">
      <div className="flex flex-col gap-6">
        <div className="text-h6 text-neutral-0 font-bold text-center">LOG IN TO YOUR ACCOUNT</div>
        <div className="flex flex-col gap-5">
          <Input
            className={LOGIN_INPUT_CLASSES}
            label="Email Address"
            required
            placeholder="Enter Email Address"
            error={errors[LOGIN_FORM_FIELDS.username]?.message}
            onKeyUp={(e) => {
              if (e.key === 'Enter') {
                handleSubmit(onSubmit)();
              }
            }}
            hasSuccessIcon={showEmailSuccessIcon}
            {...register(LOGIN_FORM_FIELDS.username)}
          />
          <Input
            className={LOGIN_INPUT_CLASSES}
            label="Password"
            required
            type="password"
            placeholder="Enter Password"
            onKeyUp={(e) => {
              if (e.key === 'Enter') {
                handleSubmit(onSubmit)();
              }
            }}
            error={errors[LOGIN_FORM_FIELDS.password]?.message}
            {...register(LOGIN_FORM_FIELDS.password)}
          />
        </div>
      </div>
      <div className="flex flex-col gap-6 items-center">
        <Button
          buttonColor="primary"
          buttonStyle="filled"
          size="lg"
          className={LOGIN_SUBMIT_BUTTON_CLASSES}
          onClick={handleSubmit(onSubmit)}
          disabled={
            mutation.isPending ||
            !watch(LOGIN_FORM_FIELDS.username) ||
            !watch(LOGIN_FORM_FIELDS.password)
          }
        >
          {mutation.isPending ? (
            <Loading02Icon className="size-6 text-opacity-light-30 animate-[spin_2s_linear_infinite]" />
          ) : (
            'Log In'
          )}
        </Button>
        <Button
          className="w-fit"
          buttonStyle="textlink"
          buttonColor="neutral"
          onClick={() => navigate(AppRoutes.FORGOT_PASSWORD)}
          disabled={mutation.isPending}
        >
          Forgot Password?
        </Button>
      </div>
    </AuthGlassContainer>
  );
};
